import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { StripeEmbeddedCheckout, createCheckoutClientSecret, createHostedCheckoutUrl } from "@/components/StripeEmbeddedCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { paymentsConfigured } from "@/lib/stripe";
import { isEmbedMode } from "@/lib/embed-mode";
import { notifyParentModalOpen } from "@/lib/embed-modal";

interface JoinFlowModalProps {
  open: boolean;
  onClose: () => void;
}

export function JoinFlowModal({ open, onClose }: JoinFlowModalProps) {
  const [step, setStep] = useState<"form" | "checkout">("form");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    salesperson: "",
  });
  const [noSalesperson, setNoSalesperson] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [waiveWithdrawal, setWaiveWithdrawal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      setStep("form");
      setForm({ firstName: "", lastName: "", email: "", phone: "", salesperson: "" });
      setNoSalesperson(false);
      setAcceptTerms(false);
      setWaiveWithdrawal(false);
      setErr("");
      setBusy(false);
      setClientSecret(null);
      return;
    }
    if (typeof document === "undefined") return;
    notifyParentModalOpen();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.phone.trim()) {
      setErr("Fyll i alla fält.");
      return;
    }
    if (!noSalesperson && !form.salesperson.trim()) {
      setErr("Fyll i säljarens namn eller bocka i att ingen säljare har hjälpt dig.");
      return;
    }
    if (!acceptTerms) {
      setErr("Du måste godkänna medlemsvillkoren och integritetspolicyn.");
      return;
    }
    if (!waiveWithdrawal) {
      setErr("Du måste bekräfta att tjänsten levereras direkt och att ångerrätten därmed upphör.");
      return;
    }
    if (!paymentsConfigured()) {
      setErr("Betalningar är inte konfigurerade ännu. Försök igen senare.");
      return;
    }
    setBusy(true);
    // Logga ut eventuell befintlig session — annars riskerar nästa person på
    // samma webbläsare att hamna i fel konto på /access/valkommen.
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      await supabase.auth.signOut();
    } catch {
      /* ignore */
    }
    // (Tidigare sparade vi lösenord i sessionStorage här — det är borta nu
    // eftersom kunden väljer sitt lösenord EFTER betalningen.)
    // Skapa checkout-sessionen INNAN vi byter steg — så att fel (t.ex. e-post
    // som redan har ett konto) visas i formuläret i stället för Stripes
    // generiska "Something went wrong".
    try {
      const signupPayload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        salesperson: noSalesperson ? null : form.salesperson.trim() || null,
      };
      // Använd absolut return-URL mot vår publika app-domän (inte iframens
      // location) så att Stripe kan skicka tillbaka kunden korrekt.
      const returnUrl = `${window.location.origin}/access/valkommen?session_id={CHECKOUT_SESSION_ID}`;

      if (isEmbedMode()) {
        // I embed-läge navigerar vi HELA toppfönstret (inte iframen) till
        // Stripes hostade checkout — Stripe blockerar iframe-embedding via
        // X-Frame-Options, vilket annars gör iframen helt vit.
        const url = await createHostedCheckoutUrl({
          priceId: "access_monthly",
          customerEmail: form.email.trim().toLowerCase(),
          signup: signupPayload,
          returnUrl,
        });
        try {
          if (window.top && window.top !== window.self) {
            window.top.location.href = url;
          } else {
            window.location.href = url;
          }
        } catch {
          // Cross-origin block på window.top.location — fall tillbaka till
          // att öppna i ny flik så användaren inte blir kvar med tom iframe.
          window.open(url, "_blank", "noopener,noreferrer");
        }
        return;
      }

      const secret = await createCheckoutClientSecret({
        priceId: "access_monthly",
        customerEmail: form.email.trim().toLowerCase(),
        signup: signupPayload,
        returnUrl,
      });
      setClientSecret(secret);
      setStep("checkout");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kunde inte starta betalningen. Försök igen.");
    } finally {
      setBusy(false);
    }
  }

  const overlayClass = isEmbedMode()
    ? "absolute inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-6 backdrop-blur-sm"
    : "fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto";

  return (
    <div className={overlayClass} role="dialog" aria-modal="true" aria-label="Bli medlem" data-embed-modal>
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-muted transition z-10"
          aria-label="Stäng"
        >
          <X size={18} />
        </button>

        {step === "form" && (
          <div className="p-6 sm:p-8">
            <span className="text-[var(--color-gold)] text-[11px] uppercase tracking-[0.18em] font-display font-semibold">
              Bli medlem
            </span>
            <h2 className="font-display font-bold text-2xl sm:text-3xl mt-2">Skapa ditt medlemskap</h2>
            <p className="text-muted-foreground text-sm mt-2 leading-relaxed">
              99 kr/månad inkl. moms. Inga bindningstider. Avsluta när du vill.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-display font-semibold mb-1.5">Förnamn</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    maxLength={50}
                    className="w-full rounded-lg border border-border px-4 py-2.5 text-sm focus:border-primary focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-display font-semibold mb-1.5">Efternamn</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    maxLength={50}
                    className="w-full rounded-lg border border-border px-4 py-2.5 text-sm focus:border-primary focus:outline-none"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-display font-semibold mb-1.5">E-post</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  maxLength={255}
                  autoComplete="email"
                  className="w-full rounded-lg border border-border px-4 py-2.5 text-sm focus:border-primary focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-display font-semibold mb-1.5">Telefonnummer</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  maxLength={20}
                  className="w-full rounded-lg border border-border px-4 py-2.5 text-sm focus:border-primary focus:outline-none"
                  placeholder="070-123 45 67"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-display font-semibold mb-1.5">
                  Har du fått hjälp av en säljare?
                </label>
                <input
                  type="text"
                  value={form.salesperson}
                  onChange={(e) => setForm({ ...form, salesperson: e.target.value })}
                  maxLength={80}
                  disabled={noSalesperson}
                  className="w-full rounded-lg border border-border px-4 py-2.5 text-sm focus:border-primary focus:outline-none disabled:bg-muted disabled:text-muted-foreground"
                  placeholder="Namn på säljare"
                />
                <label className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={noSalesperson}
                    onChange={(e) => {
                      setNoSalesperson(e.target.checked);
                      if (e.target.checked) setForm((f) => ({ ...f, salesperson: "" }));
                    }}
                    className="accent-[var(--color-primary)]"
                  />
                  <span>Ingen säljare har hjälpt mig</span>
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground -mt-2">
                Du väljer ditt lösenord direkt efter betalningen.
              </p>

              <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-[11px] text-foreground">
                <strong>3 månaders minsta löptid.</strong> Du kan säga upp när som helst — månadsavgiften (99 kr) löper fram till uppsägningstidens slut.
              </div>

              <label className="flex items-start gap-2 text-[11px] text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-0.5 accent-[var(--color-primary)]"
                />
                <span>
                  Jag godkänner{" "}
                  <Link to="/access/villkor" target="_blank" className="text-primary underline">medlemsvillkoren</Link>
                  {" "}(inkl. 3 mån minsta löptid) och har läst{" "}
                  <Link to="/access/integritetspolicy" target="_blank" className="text-primary underline">integritetspolicyn</Link>.
                </span>
              </label>

              <label className="flex items-start gap-2 text-[11px] text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={waiveWithdrawal}
                  onChange={(e) => setWaiveWithdrawal(e.target.checked)}
                  className="mt-0.5 accent-[var(--color-primary)]"
                />
                <span>
                  Jag samtycker till att tjänsten levereras direkt och är medveten om att{" "}
                  <Link to="/access/angerratt" target="_blank" className="text-primary underline">ångerrätten</Link>
                  {" "}därmed upphör enligt distansavtalslagen.
                </span>
              </label>

              {err && <p className="text-destructive text-xs">{err}</p>}

              <button
                type="submit"
                disabled={busy}
                className="w-full py-3 rounded-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white font-display font-semibold text-sm transition disabled:opacity-60"
              >
                {busy ? "Skapar konto…" : "Gå vidare till betalning"}
              </button>
              <p className="text-[11px] text-muted-foreground text-center">
                Kortuppgifter hanteras enbart av Stripe — vi lagrar inga kortuppgifter.
              </p>
            </form>
          </div>
        )}

        {step === "checkout" && clientSecret && (
          <div>
            <PaymentTestModeBanner />
            <div className="p-4 sm:p-6">
              <h2 className="font-display font-bold text-xl mb-3">Slutför betalning</h2>
              <StripeEmbeddedCheckout
                priceId="access_monthly"
                clientSecret={clientSecret}
                returnUrl={`${window.location.origin}/access/valkommen?session_id={CHECKOUT_SESSION_ID}`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
